import { ChangeDetectionStrategy, Component, inject, Injectable, signal, ViewEncapsulation } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { Observable } from 'rxjs';
import { SafeHTMLPipe } from '../../pipes/safehtml';
import { ScreenSizeService } from '../../../../services/screensize/screensize.service';


@Component({
    selector: 'dialog-confirmation-component',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        SafeHTMLPipe
    ],
    host: {
        class: 'block relative',
    },
    styleUrls: ['confirmation-dialog.component.scss'],
    templateUrl: 'confirmation-dialog.component.html',
})
export class ConfirmationDialogComponent {
    // Injects
    protected readonly dialogRef = inject(MatDialogRef<ConfirmationDialogComponent>);
    protected readonly data = inject<ConfirmationDialogOptions>(MAT_DIALOG_DATA);
    // protected readonly pcpsDefaults = inject<PCPSDialogsDefaultOptions>(
    //     PCPS_DIALOGS_DEFAULT_OPTIONS,
    //     { optional: true },
    // );
    protected readonly screenSizeSvc = inject(ScreenSizeService);
    
    // #region Properties

    protected readonly canceltext = this.data?.CancelText ?? 'Cancel';
    protected readonly message = signal(this.data?.MessageText ?? '');
    protected readonly oktext = this.data?.OKText ?? 'OK';
    protected readonly title = this.data?.TitleText ?? 'Information';

    // #endregion

    // #region Cancel Handler

    CancelHandler() {
        this.dialogRef.close(null);
    }

    // #endregion

    // #region OK Handler

    OKHandler() {
        this.dialogRef.close(true);
    }

    // #endregion
}

@Injectable({ providedIn: 'root' })
export class ConfirmationDialogService {
    // #region Injects

    protected readonly matDialog = inject(MatDialog);
    protected readonly screensizeService = inject(ScreenSizeService);
    protected readonly defaultOpts = inject<MatDialogConfig>(MAT_DIALOG_DEFAULT_OPTIONS, {
        optional: true,
    });

    // #endregion

    // #region Properties

    protected readonly smallMediaSize = this.screensizeService.IsSmallDisplay || false;

    // #endregion

    // #region Close All Dialogs Service Call

    CloseAllDialogs(): void {
        this.matDialog.closeAll();
    }

    // #endregion

    // #region Open

    Open(options?: ConfirmationDialogOptions): Observable<any | null> {
        const dlgoptions: ConfirmationDialogOptions = {
            data: {
                TitleText: 'Confirm Request',
                MessageText: '',
                ...options,
            },
            // Passthrus
            id: options?.id ?? this.defaultOpts?.id,
            role: options?.role ?? this.defaultOpts?.role ?? 'dialog',
            panelClass: !!options?.panelClass
                ? Array.isArray(options.panelClass)
                    ? options.panelClass
                    : [options.panelClass]
                : (this.defaultOpts?.panelClass ?? ''),
            hasBackdrop: options?.hasBackdrop ?? this.defaultOpts?.hasBackdrop ?? true,
            backdropClass: options?.backdropClass ?? this.defaultOpts?.backdropClass ?? '',
            disableClose:
                options?.disableClose != null
                    ? options.disableClose
                    : (this.defaultOpts?.disableClose ?? false),
            width: options?.width ?? this.defaultOpts?.width ?? '',
            height: options?.height ?? this.defaultOpts?.height ?? '',
            minWidth: options?.minWidth ?? this.defaultOpts?.minWidth,
            minHeight: options?.minHeight ?? this.defaultOpts?.minHeight,
            maxWidth: options?.maxWidth ?? this.defaultOpts?.maxWidth ?? '80vw',
            maxHeight: options?.maxHeight ?? this.defaultOpts?.maxHeight,
            position: options?.position ?? this.defaultOpts?.position,
            direction: options?.direction ?? this.defaultOpts?.direction,
            ariaDescribedBy: options?.ariaDescribedBy ?? this.defaultOpts?.ariaDescribedBy ?? null,
            ariaLabelledBy: options?.ariaLabelledBy ?? this.defaultOpts?.ariaLabelledBy ?? null,
            ariaLabel: options?.ariaLabel ?? this.defaultOpts?.ariaLabel ?? null,
            ariaModal: options?.ariaModal ?? this.defaultOpts?.ariaModal ?? true,
            autoFocus: options?.autoFocus ?? this.defaultOpts?.autoFocus ?? 'first-tabbable',
            restoreFocus: options?.restoreFocus ?? this.defaultOpts?.restoreFocus ?? true,
            delayFocusTrap: options?.delayFocusTrap ?? this.defaultOpts?.delayFocusTrap ?? true,
            scrollStrategy: options?.scrollStrategy ?? this.defaultOpts?.scrollStrategy,
            closeOnNavigation:
                options?.closeOnNavigation ?? this.defaultOpts?.closeOnNavigation ?? true,
            enterAnimationDuration:
                options?.enterAnimationDuration ?? this.defaultOpts?.enterAnimationDuration,
            exitAnimationDuration:
                options?.exitAnimationDuration ?? this.defaultOpts?.exitAnimationDuration,
        };

        const openHandle = this.matDialog.open<ConfirmationDialogComponent>(
            ConfirmationDialogComponent,
            dlgoptions,
        );

        return openHandle.afterClosed();
    }

    // #endregion
}

// #region Custom Interfaces

export interface ConfirmationDialogOptions extends MatDialogConfig {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    IconColor?: string;
    IconName?: string;
    ShowIcon?: boolean;
}

// #endregion